import {
  EXPORT_MAX_ROWS,
  FINANCING_LABELS,
  QUOTE_STATUS_LABELS,
  QuoteStatus,
  type QuoteDto,
} from '@performance-crm/shared';
import { Download, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ListFooter } from '@/components/commercial/ListFooter';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast-context';
import { fmt, todayInTz } from '@/lib/format';
import { fetchAllPages, useAdvisors, useQuotes, type QuoteListParams } from '@/lib/queries';
import { downloadWorkbook } from '@/lib/spreadsheet';
import { useCompanyTimezone } from '@/lib/useCompanyTimezone';
import { useDebounced } from '@/lib/useDebounced';
import { quotesSheet } from './commercialExport';

const TONE: Record<string, 'success' | 'muted' | 'danger' | 'primary'> = {
  VIGENTE: 'success',
  EXPIRADA: 'muted',
  ANULADA: 'danger',
  CONVERTIDA: 'primary',
};

/** Proformas de la empresa: búsqueda, estado, fechas de emisión (días de la empresa), asesor, «Cargar más» y Excel. */
export function QuotesTab({ companyId, projectId }: { companyId: string; projectId?: string }) {
  const tz = useCompanyTimezone(companyId);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [advisorId, setAdvisorId] = useState('');
  const [exporting, setExporting] = useState(false);
  const toast = useToast();
  const { data: advisors } = useAdvisors(companyId);
  const needle = useDebounced(q.trim(), 300);
  // Las fechas viajan tal cual (AAAA-MM-DD): la API las resuelve como días completos de la zona de la empresa,
  // con el «hasta» inclusivo, igual que Caídas y cierre.
  const params: QuoteListParams = {
    companyId,
    projectId,
    status: status || undefined,
    q: needle.length >= 2 ? needle : undefined,
    from: from || undefined,
    to: to || undefined,
    advisorId: advisorId || undefined,
  };
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useQuotes(params);
  const items = data?.pages.flatMap((p) => p.items) ?? [];
  const total = data?.pages[0]?.total;
  const exportExcel = async () => {
    setExporting(true);
    try {
      const all = await fetchAllPages<QuoteDto>('/api/v1/quotes', params);
      await downloadWorkbook(
        [quotesSheet(all.items, tz)],
        `proformas-${from || 'inicio'}_a_${to || todayInTz(tz)}.xlsx`,
      );
      if (all.truncated)
        toast.error(
          'Excel incompleto',
          `Se exportaron las primeras ${fmt.number(EXPORT_MAX_ROWS)} proformas: acota las fechas para el resto.`,
        );
    } catch (err) {
      toast.error('No se pudo generar el Excel', (err as Error).message);
    } finally {
      setExporting(false);
    }
  };
  return (
    <div className="space-y-3">
      <div className="bg-card flex flex-wrap items-center gap-2 rounded-lg border p-3">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Código o cliente…"
          aria-label="Buscar proforma"
          className="h-9 w-56"
        />
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label="Estado de proforma"
          className="h-9 w-44"
        >
          <option value="">Todos los estados</option>
          {Object.values(QuoteStatus).map((s) => (
            <option key={s} value={s}>
              {QUOTE_STATUS_LABELS[s]}
            </option>
          ))}
        </Select>
        <Input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          aria-label="Emitida desde"
          className="h-9 w-40"
        />
        <span className="text-muted-foreground text-sm">a</span>
        <Input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          aria-label="Emitida hasta"
          className="h-9 w-40"
        />
        <Select
          value={advisorId}
          onChange={(e) => setAdvisorId(e.target.value)}
          aria-label="Asesor"
          className="h-9 w-52"
        >
          <option value="">Todos los asesores</option>
          {(advisors ?? [])
            .filter((a) => a.kind === 'HUMAN')
            .map((a) => (
              <option key={a.id} value={a.id}>
                {a.fullName}
              </option>
            ))}
        </Select>
        <div className="ml-auto flex items-center gap-2">
          {data && (
            <p className="text-muted-foreground text-sm" data-testid="quotes-total">
              {fmt.number(total ?? items.length)} proformas
            </p>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => void exportExcel()}
            disabled={!data || items.length === 0 || exporting}
            data-testid="export-quotes"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}{' '}
            Exportar Excel
          </Button>
        </div>
      </div>
      <p className="text-muted-foreground text-xs">
        Las fechas filtran por el día de emisión en la zona de la empresa; el asesor es el actual
        del lead.
      </p>
      {isLoading && <Skeleton className="h-64 w-full" />}
      {data && (
        <div className="bg-card overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-left text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2">Código</th>
                <th className="px-3 py-2">Cliente</th>
                <th className="px-3 py-2">Proyecto</th>
                <th className="px-3 py-2">Unidad</th>
                <th className="px-2 py-2 text-right">Precio</th>
                <th className="px-3 py-2">Financ.</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2">Emitida</th>
                <th className="px-3 py-2">Vence</th>
                <th className="px-3 py-2">Asesor</th>
              </tr>
            </thead>
            <tbody>
              {items.map((qt) => (
                <tr key={qt.id} className="border-t" data-testid="quote-list-row">
                  <td className="px-3 py-2 font-medium">{qt.code}</td>
                  <td className="px-3 py-2">
                    <Link to={`/leads/${qt.leadId}`} className="text-primary hover:underline">
                      {qt.contactName}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{qt.projectName}</td>
                  <td className="px-3 py-2">{qt.unitCode ?? '—'}</td>
                  <td className="px-2 py-2 text-right">
                    {fmt.money(qt.price, qt.currency)}
                    {Number(qt.discount) > 0 && (
                      <span className="text-muted-foreground block text-xs">
                        dscto {fmt.money(qt.discount, qt.currency)}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {qt.financingType ? FINANCING_LABELS[qt.financingType] : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={TONE[qt.status] ?? 'muted'}>
                      {QUOTE_STATUS_LABELS[qt.status] ?? qt.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">{fmt.date(qt.createdAt)}</td>
                  <td className="px-3 py-2">{fmt.date(qt.expiresAt)}</td>
                  <td className="px-3 py-2">{qt.advisorName ?? '—'}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-muted-foreground px-3 py-8 text-center">
                    Sin proformas con estos filtros.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <ListFooter
        shown={items.length}
        total={total}
        noun="proformas"
        hasMore={!!hasNextPage}
        loading={isFetchingNextPage}
        onMore={() => void fetchNextPage()}
        testId="quotes-footer"
      />
    </div>
  );
}
